import React, { useState, useEffect } from 'react';
import PageEditor from './pageEditor';
import { Page } from './types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

const GET_PAGE = gql`
  query GetPage($id: Int!) {
    page(id: $id) {
      dbId
      title
      sections
    }
  }
`;

const CREATE_UPDATE_PAGE = gql`
  mutation UpsertPage($title: String!, $sections: [SectionInput!]!, $id: ID) {
    upsertPage(title: $title, sections: $sections, id: $id) {
      page {
        id
        dbId
        title
        slug
        sections
      }
      success
      errors
    }
  }
`;

const PageEditorApp: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get('pageId');
  const [page, setPage] = useState<Page>({
    title: '',
    sections: []
  });

  const { data: pageData, loading: pageLoading } = useQuery(GET_PAGE, {
    variables: { id: pageId ? parseInt(pageId) : 0 },
    skip: !pageId,
  });

  const [createUpdatePage] = useMutation(CREATE_UPDATE_PAGE);

  useEffect(() => {
    if (pageData?.page) {
      try {
        // Parse the sections string into an array
        const parsedSections = JSON.parse(pageData.page.sections);
        
        setPage({
          id: pageData.page.dbId.toString(), // Convert to string for ID type
          title: pageData.page.title,
          sections: parsedSections
        });
      } catch (error) {
        console.error('Error parsing sections:', error);
        setError('Error loading page data');
      }
    }
  }, [pageData]);

  const handleSubmit = async (page: Page) => {
    console.log('Submitting page:', page);
    setLoading(true);
    setError(null);

    try {
      // Transform sections to match the expected format
      const transformedSections = page.sections.map(section => ({
        sectionRefs: section.sectionRefs,
        data: JSON.stringify(section.data) // Convert data to string as it's a JSONField in Django
      }));

      const { data } = await createUpdatePage({
        variables: {
          title: page.title,
          sections: transformedSections,
          id: page.id // ID type expects string
        },
      });

      if (data.upsertPage.errors && data.upsertPage.errors.length > 0) {
        setError(data.upsertPage.errors[0]);
      } else {
        toast.success(pageId ? 'Page updated successfully' : 'Page created successfully');
        navigate(`/dashboard/pages/editor/?pageId=${data.upsertPage.page.dbId}`);
      }
    } catch (error: any) {
      console.error('Error saving page:', error);
      setError(error.message || 'An error occurred while saving the page.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-[#277200] bg-opacity-10 px-6 py-4 border-b border-gray-100">
            <h1 className="text-2xl font-bold text-[#141F28]">
              Page Editor <span className='text-sm text-gray-500'>{pageId ? 'Edit Page' : 'Create Page'}</span>
            </h1>
          </div>
          <PageEditor 
            onSubmit={handleSubmit}
            loading={loading || pageLoading}
            error={error || undefined}
            initialPage={page}
            isEditMode={!!pageId}
          />
        </div>
      </div>
    </div>
  );
};

export default PageEditorApp;